import React, { useState, useEffect, useCallback } from 'react';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1} ${String(d.getHours()).padStart(2, '0')}:00`;
}

function authRedirect(err) {
  if (err?.status === 401) {
    setSuperAdminToken(null);
    window.location.href = '/admin';
    return true;
  }
  return false;
}

export default function AdminErrorMonitoring() {
  const [tab, setTab] = useState('overview');
  const [eventsFingerprint, setEventsFingerprint] = useState(() =>
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('errors_filter_fingerprint') || '' : ''
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-800 mb-2">Monitoramento de erros</h1>
      <p className="text-sm text-slate-600 mb-6">
        Análise de falhas da API, fila de alertas por e-mail e destinatários.
      </p>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="events">Eventos</TabsTrigger>
          <TabsTrigger value="groups">Grupos</TabsTrigger>
          <TabsTrigger value="queue">Fila</TabsTrigger>
          <TabsTrigger value="alerts">Alertas</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewPanel />
        </TabsContent>
        <TabsContent value="events" className="mt-4">
          <EventsPanel
            fingerprintFilter={eventsFingerprint}
            onFingerprintConsumed={() => {
              setEventsFingerprint('');
              sessionStorage.removeItem('errors_filter_fingerprint');
            }}
          />
        </TabsContent>
        <TabsContent value="groups" className="mt-4">
          <GroupsPanel onPickFingerprint={(fp) => {
            sessionStorage.setItem('errors_filter_fingerprint', fp);
            setEventsFingerprint(fp);
            setTab('events');
            toast('Filtro por fingerprint aplicado na aba Eventos.');
          }} />
        </TabsContent>
        <TabsContent value="queue" className="mt-4">
          <QueuePanel />
        </TabsContent>
        <TabsContent value="alerts" className="mt-4">
          <AlertsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [erro, setErro] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
      const qs = `?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
      const res = await apiAdmin.get(`/api/admin/errors/summary${qs}`);
      setData(res);
    } catch (err) {
      if (authRedirect(err)) return;
      setErro(getDisplayMessage(err, 'Erro ao carregar resumo.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
      </div>
    );
  }

  const chartData = (data?.series || []).map((p) => ({
    ...p,
    label: formatShortDate(p.t),
  }));

  return (
    <div className="space-y-6">
      {erro && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{erro}</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Últimas 24h</p>
          <p className="text-2xl font-semibold text-slate-800">{data?.totalLast24h ?? 0}</p>
        </div>
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Últimos 7 dias</p>
          <p className="text-2xl font-semibold text-slate-800">{data?.totalLast7d ?? 0}</p>
        </div>
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase">Jobs pendentes</p>
          <p className="text-2xl font-semibold text-amber-700">{data?.pendingJobs ?? 0}</p>
        </div>
        <div className="rounded-lg border bg-white p-4 shadow-sm flex items-center">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            Atualizar
          </Button>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <h2 className="text-sm font-medium text-slate-700 mb-4">Volume por hora (intervalo selecionado)</h2>
        <div className="h-[280px] w-full">
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-500">Sem dados no período.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#475569" strokeWidth={2} dot={false} name="Erros" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-medium text-slate-700 mb-2">Top módulos</h3>
          <ul className="text-sm space-y-1">
            {(data?.topModules || []).map((m) => (
              <li key={m.module} className="flex justify-between gap-2">
                <span className="truncate text-slate-700">{m.module}</span>
                <span className="text-slate-500 shrink-0">{m.count}</span>
              </li>
            ))}
            {(!data?.topModules || data.topModules.length === 0) && (
              <li className="text-slate-500">Nenhum</li>
            )}
          </ul>
        </div>
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-medium text-slate-700 mb-2">Top rotas</h3>
          <ul className="text-sm space-y-1">
            {(data?.topPaths || []).map((p) => (
              <li key={p.path} className="flex justify-between gap-2">
                <span className="truncate text-slate-600 font-mono text-xs" title={p.path}>{p.path}</span>
                <span className="text-slate-500 shrink-0">{p.count}</span>
              </li>
            ))}
            {(!data?.topPaths || data.topPaths.length === 0) && (
              <li className="text-slate-500">Nenhum</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function EventsPanel({ fingerprintFilter, onFingerprintConsumed }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({
    q: '',
    module: '',
    fingerprint: '',
    correlationId: '',
    empresaId: '',
  });

  const pageSize = 20;

  useEffect(() => {
    if (!fingerprintFilter) return;
    setFilters((f) => ({ ...f, fingerprint: fingerprintFilter }));
    setPage(1);
    onFingerprintConsumed?.();
  }, [fingerprintFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (filters.q) params.set('q', filters.q);
      if (filters.module) params.set('module', filters.module);
      if (filters.fingerprint) params.set('fingerprint', filters.fingerprint);
      if (filters.correlationId) params.set('correlationId', filters.correlationId);
      if (filters.empresaId) params.set('empresaId', filters.empresaId);
      const res = await apiAdmin.get(`/api/admin/errors/events?${params}`);
      setItems(res.items || []);
      setTotal(res.total ?? 0);
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao listar eventos.'));
    } finally {
      setLoading(false);
    }
  }, [page, filters.q, filters.module, filters.fingerprint, filters.correlationId, filters.empresaId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    try {
      const ev = await apiAdmin.get(`/api/admin/errors/events/${id}`);
      setDetail(ev);
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao carregar detalhe.'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div>
          <Label className="text-xs">Busca (mensagem/rota)</Label>
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Texto" />
        </div>
        <div>
          <Label className="text-xs">Módulo</Label>
          <Input value={filters.module} onChange={(e) => setFilters((f) => ({ ...f, module: e.target.value }))} />
        </div>
        <div>
          <Label className="text-xs">Fingerprint</Label>
          <Input value={filters.fingerprint} onChange={(e) => setFilters((f) => ({ ...f, fingerprint: e.target.value }))} className="font-mono text-xs" />
        </div>
        <div>
          <Label className="text-xs">Correlation ID</Label>
          <Input value={filters.correlationId} onChange={(e) => setFilters((f) => ({ ...f, correlationId: e.target.value }))} />
        </div>
        <div>
          <Label className="text-xs">Empresa ID</Label>
          <Input value={filters.empresaId} onChange={(e) => setFilters((f) => ({ ...f, empresaId: e.target.value }))} className="font-mono text-xs" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => { setPage(1); load(); }}>Filtrar</Button>
        <Button size="sm" variant="outline" onClick={() => {
          setFilters({ q: '', module: '', fingerprint: '', correlationId: '', empresaId: '' });
          sessionStorage.removeItem('errors_filter_fingerprint');
          setPage(1);
        }}>Limpar</Button>
      </div>

      <div className="rounded-lg border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-2">Quando</th>
              <th className="text-left p-2">Módulo</th>
              <th className="text-left p-2">Mensagem</th>
              <th className="text-left p-2">Rota</th>
              <th className="text-left p-2">Usuário</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500">Carregando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500">Nenhum evento</td></tr>
            ) : (
              items.map((row) => (
                <tr key={row.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(row.id)}>
                  <td className="p-2 whitespace-nowrap text-xs">{new Date(row.occurredAt).toLocaleString('pt-BR')}</td>
                  <td className="p-2">{row.module}</td>
                  <td className="p-2 max-w-xs truncate" title={row.message}>{row.message}</td>
                  <td className="p-2 font-mono text-xs max-w-[180px] truncate" title={row.path}>{row.method} {row.path}</td>
                  <td className="p-2 text-xs truncate max-w-[140px]" title={row.usuarioEmail}>{row.usuarioEmail || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center text-sm text-slate-600">
        <span>Total: {total}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span>Página {page}</span>
          <Button size="sm" variant="outline" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhe do erro</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <p><strong>Correlation ID:</strong> <code className="text-xs bg-slate-100 px-1">{detail.correlationId}</code></p>
              <p><strong>Módulo:</strong> {detail.module} · <strong>Ambiente:</strong> {detail.environment}</p>
              <p><strong>Endpoint:</strong> <code>{detail.method} {detail.path}</code></p>
              <p><strong>Mensagem:</strong></p>
              <pre className="bg-amber-50 p-2 rounded text-xs whitespace-pre-wrap break-words">{detail.message}</pre>
              {detail.stack && (
                <>
                  <p><strong>Stack:</strong></p>
                  <pre className="bg-slate-100 p-2 rounded text-xs max-h-64 overflow-auto whitespace-pre-wrap">{detail.stack}</pre>
                </>
              )}
              {detail.payloadSanitized != null && (
                <>
                  <p><strong>Payload (sanitizado):</strong></p>
                  <pre className="bg-slate-50 p-2 rounded text-xs max-h-48 overflow-auto">
                    {JSON.stringify(detail.payloadSanitized, null, 2)}
                  </pre>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupsPanel({ onPickFingerprint }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
      const qs = `?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
      const res = await apiAdmin.get(`/api/admin/errors/groups${qs}`);
      setItems(res.items || []);
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao carregar grupos.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <Button size="sm" variant="outline" onClick={load} disabled={loading}>Atualizar</Button>
      <div className="rounded-lg border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-2">Ocorrências</th>
              <th className="text-left p-2">Módulo</th>
              <th className="text-left p-2">Primeira / Última</th>
              <th className="text-left p-2">Amostra</th>
              <th className="text-left p-2">Fingerprint</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center">Carregando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500">Nenhum grupo</td></tr>
            ) : (
              items.map((g) => (
                <tr key={g.fingerprint} className="border-b">
                  <td className="p-2 font-medium">{g.count}</td>
                  <td className="p-2">{g.module || '—'}</td>
                  <td className="p-2 text-xs whitespace-nowrap">
                    {new Date(g.firstAt).toLocaleString('pt-BR')}
                    <br />
                    {new Date(g.lastAt).toLocaleString('pt-BR')}
                  </td>
                  <td className="p-2 max-w-xs truncate" title={g.sampleMessage}>{g.sampleMessage}</td>
                  <td className="p-2">
                    <Button size="sm" variant="secondary" className="font-mono text-[10px]" onClick={() => onPickFingerprint(g.fingerprint)}>
                      Filtrar eventos
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QueuePanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (status) params.set('status', status);
      const res = await apiAdmin.get(`/api/admin/errors/notification-jobs?${params}`);
      setItems(res.items || []);
      setTotal(res.total ?? 0);
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao carregar fila.'));
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = async (id) => {
    try {
      await apiAdmin.post(`/api/admin/errors/notification-jobs/${id}/retry`, {});
      toast.success('Job reenfileirado.');
      load();
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Falha ao reenfileirar.'));
    }
  };

  const runWorker = async () => {
    try {
      const r = await apiAdmin.post('/api/admin/errors/process-queue', {});
      toast.success(`Processado: ${r.processed}, enviados: ${r.sent}, falhas: ${r.failed}`);
      load();
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Falha ao processar fila.'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <Label className="text-xs">Status</Label>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          >
            <option value="">Todos</option>
            <option value="pending">pending</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
          </select>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>Atualizar</Button>
        <Button size="sm" onClick={runWorker}>Processar fila agora</Button>
      </div>
      <div className="rounded-lg border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Tentativas</th>
              <th className="text-left p-2">Próxima execução</th>
              <th className="text-left p-2">Evento</th>
              <th className="text-left p-2">Erro</th>
              <th className="text-left p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center">Carregando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">Nenhum job</td></tr>
            ) : (
              items.map((j) => (
                <tr key={j.id} className="border-b">
                  <td className="p-2">{j.status}</td>
                  <td className="p-2">{j.attempts}</td>
                  <td className="p-2 text-xs">{new Date(j.nextRunAt).toLocaleString('pt-BR')}</td>
                  <td className="p-2 text-xs max-w-[200px] truncate" title={j.errorEvent?.message}>
                    {j.errorEvent?.module}: {j.errorEvent?.message?.slice(0, 60)}
                  </td>
                  <td className="p-2 text-xs text-red-600 max-w-[200px] truncate" title={j.lastError}>{j.lastError || '—'}</td>
                  <td className="p-2">
                    {(j.status === 'failed' || j.status === 'pending') && (
                      <Button size="sm" variant="outline" onClick={() => retry(j.id)}>Reenviar</Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="text-sm text-slate-600">Total: {total}</div>
    </div>
  );
}

function AlertsPanel() {
  const [recipients, setRecipients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        apiAdmin.get('/api/admin/errors/recipients'),
        apiAdmin.get('/api/admin/errors/settings'),
      ]);
      setRecipients(Array.isArray(r) ? r : []);
      setSettings({
        ...s,
        envsText: Array.isArray(s.envs) ? s.envs.join(', ') : 'production',
      });
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao carregar alertas.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addRecipient = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setSaving(true);
    try {
      await apiAdmin.post('/api/admin/errors/recipients', { email: newEmail.trim(), ativo: true });
      setNewEmail('');
      toast.success('Destinatário adicionado.');
      load();
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Não foi possível adicionar.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleRecipient = async (id, ativo) => {
    try {
      await apiAdmin.put(`/api/admin/errors/recipients/${id}`, { ativo: !ativo });
      load();
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao atualizar.'));
    }
  };

  const deleteRecipient = async (id) => {
    if (!window.confirm('Remover este e-mail?')) return;
    try {
      await apiAdmin.delete(`/api/admin/errors/recipients/${id}`);
      toast.success('Removido.');
      load();
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao remover.'));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      const envsStr = (settings.envsText || '').split(',').map((s) => s.trim()).filter(Boolean);
      await apiAdmin.put('/api/admin/errors/settings', {
        enabled: settings.enabled,
        minLevel: settings.minLevel,
        envs: envsStr.length ? envsStr : ['production'],
        rateWindowSec: Number(settings.rateWindowSec) || 3600,
        rateMaxPerFingerprint: Number(settings.rateMaxPerFingerprint) || 3,
      });
      toast.success('Configurações salvas.');
      load();
    } catch (err) {
      if (authRedirect(err)) return;
      toast.error(getDisplayMessage(err, 'Erro ao salvar.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
      </div>
    );
  }

  const s = settings || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h2 className="text-lg font-medium text-slate-800 mb-4">Destinatários</h2>
        <form onSubmit={addRecipient} className="flex gap-2 mb-4">
          <Input
            type="email"
            placeholder="novo@email.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="max-w-sm"
          />
          <Button type="submit" disabled={saving}>Adicionar</Button>
        </form>
        <ul className="space-y-2">
          {recipients.map((rec) => (
            <li key={rec.id} className="flex items-center justify-between gap-2 border rounded-md p-3 bg-white">
              <span className="text-sm truncate">{rec.email}</span>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant={rec.ativo ? 'secondary' : 'outline'} onClick={() => toggleRecipient(rec.id, rec.ativo)}>
                  {rec.ativo ? 'Ativo' : 'Inativo'}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteRecipient(rec.id)}>Excluir</Button>
              </div>
            </li>
          ))}
          {recipients.length === 0 && <p className="text-sm text-slate-500">Nenhum destinatário. Os alertas por e-mail só são enviados se houver e-mail ativo e SMTP configurado.</p>}
        </ul>
      </div>

      <div>
        <h2 className="text-lg font-medium text-slate-800 mb-4">Configuração global</h2>
        <form onSubmit={saveSettings} className="space-y-4 max-w-md">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ea_enabled"
              checked={!!s.enabled}
              onChange={(e) => setSettings((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            <Label htmlFor="ea_enabled">Alertas por e-mail ativos</Label>
          </div>
          <div>
            <Label>Nível mínimo para notificar</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm mt-1"
              value={s.minLevel || 'error'}
              onChange={(e) => setSettings((prev) => ({ ...prev, minLevel: e.target.value }))}
            >
              <option value="error">error (todos os erros HTTP / integração)</option>
              <option value="critical">critical (apenas falhas graves do processo)</option>
            </select>
          </div>
          <div>
            <Label>Ambientes (NODE_ENV) em que enviar e-mail</Label>
            <Input
              className="mt-1"
              placeholder="production, development"
              value={s.envsText != null ? s.envsText : (Array.isArray(s.envs) ? s.envs.join(', ') : '')}
              onChange={(e) => setSettings((prev) => ({ ...prev, envsText: e.target.value }))}
            />
            <p className="text-xs text-slate-500 mt-1">Separados por vírgula. Padrão em produção: apenas production.</p>
          </div>
          <div>
            <Label>Janela de rate limit (segundos)</Label>
            <Input
              type="number"
              min={60}
              className="mt-1"
              value={s.rateWindowSec ?? 3600}
              onChange={(e) => setSettings((prev) => ({ ...prev, rateWindowSec: e.target.value }))}
            />
          </div>
          <div>
            <Label>Máx. e-mails por fingerprint na janela</Label>
            <Input
              type="number"
              min={1}
              className="mt-1"
              value={s.rateMaxPerFingerprint ?? 3}
              onChange={(e) => setSettings((prev) => ({ ...prev, rateMaxPerFingerprint: e.target.value }))}
            />
          </div>
          <Button type="submit" disabled={saving}>Salvar configurações</Button>
        </form>
      </div>
    </div>
  );
}
